/**
 * The Ada bridge's stay cables, as a 22-string harp — which Avalski toranj's
 * beacon can turn into an electric guitar, a chiptune pulse channel or a music
 * box.
 *
 * Every voice is synthesised, not sampled: each note is rendered into an
 * AudioBuffer once and replayed from there. `prepare` renders a voice's strings
 * in the background when it is picked; a pluck that gets there first renders its
 * own string on the spot.
 */

/** C major, because that is what a 22-string lever harp is tuned to: C3 to C6. */
const SCALE = [0, 2, 4, 5, 7, 9, 11];
const ROOT_MIDI = 48; // C3
export const STRING_COUNT = 22;

/** MIDI note for string `i`, counting from the long cable on the left. */
export function noteOf(i: number): number {
  return ROOT_MIDI + 12 * Math.floor(i / 7) + (SCALE[i % 7] ?? 0);
}
const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

/** What the cables sound like. Avalski toranj's beacon picks one. */
export type Voice = 'harp' | 'guitar' | 'chip' | 'musicbox';

const noise = () => Math.random() * 2 - 1;

// --- shared pieces -----------------------------------------------------------

/**
 * One Karplus-Strong string, dead in tune: fill a ring buffer one wavelength
 * long with an excitation, then read it out on a loop, replacing each sample
 * with the average of it and the one before. That averaging is a one-pole
 * lowpass inside the loop, so the partials die off in the order a real
 * string's do — bright attack, warm tail.
 *
 * `excite` writes one period of whatever plucked the string into the ring; DC
 * and level are normalised here, so every string starts at the same peak.
 */
function karplus(
  ctx: BaseAudioContext,
  freq: number,
  tail: number,
  excite: (ring: Float32Array) => void,
): AudioBuffer {
  // The ring has to be a whole number of samples, which at C6 is 42 of them —
  // so rounding it to the context's rate lands up to 20 cents off, and the top
  // octave plays out of tune. Instead the ring picks the length and the BUFFER
  // gets whatever sample rate makes that length exactly one period; the source
  // node resamples it on playback and every note is dead on. The +0.5 is the
  // averaging filter's own half-sample of delay, which is part of the loop.
  const N = Math.max(2, Math.round(ctx.sampleRate / freq));
  const sr = Math.min(96000, Math.max(8000, (N + 0.5) * freq));
  const n = Math.ceil(sr * tail);

  const ring = new Float32Array(N);
  excite(ring);
  // Any DC left in the ring survives every loop pass as a thump, and the peak
  // decides how loud the note is — so both get normalised out before playing.
  let mean = 0;
  for (let k = 0; k < N; k++) mean += ring[k] ?? 0;
  mean /= N;
  let peak = 1e-6;
  for (let k = 0; k < N; k++) {
    const v = (ring[k] ?? 0) - mean;
    ring[k] = v;
    peak = Math.max(peak, Math.abs(v));
  }
  for (let k = 0; k < N; k++) ring[k] = (ring[k] ?? 0) / peak;

  // Loop gain. The fundamental passes through the ring `freq` times a second,
  // so this — not an envelope laid over the top — is what sets the decay.
  const R = Math.exp(Math.log(0.001) / (tail * freq));
  const buf = ctx.createBuffer(1, n, sr);
  const out = buf.getChannelData(0);
  let idx = 0;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const cur = ring[idx] ?? 0;
    out[i] = cur;
    ring[idx] = (cur + prev) * 0.5 * R;
    prev = cur;
    idx = idx + 1 === N ? 0 : idx + 1;
  }
  return buf;
}

/** Noise with the top taken off: `bite` near 1 is a pick, lower is a finger. */
function pluckNoise(ring: Float32Array, bite: number): void {
  let lp = 0;
  for (let k = 0; k < ring.length; k++) {
    lp += bite * (noise() - lp);
    ring[k] = lp;
  }
}

/**
 * Soften both ends: `atk` samples of fade-in so the first sample is not a step,
 * and a fade across the last `rel` seconds so the buffer does not end on a
 * click.
 */
function fadeEnds(out: Float32Array, sr: number, atk = 96, rel = 0.03): void {
  const n = out.length;
  const a = Math.min(atk, n);
  for (let i = 0; i < a; i++) out[i] = (out[i] ?? 0) * (i / a);
  const r = Math.min(Math.round(sr * rel), n);
  for (let i = 0; i < r; i++) out[n - 1 - i] = (out[n - 1 - i] ?? 0) * (i / r);
}

/** One-pole highpass, in place. */
function highpass(out: Float32Array, sr: number, cutoff: number): void {
  const a = 1 / (1 + (2 * Math.PI * cutoff) / sr);
  let x1 = 0;
  let y1 = 0;
  for (let i = 0; i < out.length; i++) {
    const x = out[i] ?? 0;
    y1 = a * (y1 + x - x1);
    x1 = x;
    out[i] = y1;
  }
}

/** Resonant two-pole lowpass (RBJ cookbook), in place. */
function lowpass(out: Float32Array, sr: number, cutoff: number, q: number): void {
  const w = (2 * Math.PI * cutoff) / sr;
  const cw = Math.cos(w);
  const alpha = Math.sin(w) / (2 * q);
  const a0 = 1 + alpha;
  const b0 = (1 - cw) / 2 / a0;
  const b1 = (1 - cw) / a0;
  const a1 = (-2 * cw) / a0;
  const a2 = (1 - alpha) / a0;
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < out.length; i++) {
    const x = out[i] ?? 0;
    const y = b0 * (x + x2) + b1 * x1 - a1 * y1 - a2 * y2;
    x2 = x1;
    x1 = x;
    y2 = y1;
    y1 = y;
    out[i] = y;
  }
}

function normalise(out: Float32Array, to = 1): void {
  let peak = 1e-9;
  for (let i = 0; i < out.length; i++) peak = Math.max(peak, Math.abs(out[i] ?? 0));
  const g = to / peak;
  for (let i = 0; i < out.length; i++) out[i] = (out[i] ?? 0) * g;
}

// --- red: the harp ------------------------------------------------------------

function renderHarp(ctx: BaseAudioContext, freq: number): AudioBuffer {
  // Long strings ring longer.
  const tail = Math.min(3.2, Math.max(1, 3.2 * (130 / freq) ** 0.35));
  // White noise is a snare hit; lowpassing it first takes the top off the
  // attack, which is the difference between a plucked string and a plucked
  // rubber band.
  const buf = karplus(ctx, freq, tail, (ring) => pluckNoise(ring, 0.4));
  fadeEnds(buf.getChannelData(0), buf.sampleRate);
  return buf;
}

// --- yellow: grunge guitar ------------------------------------------------------

/**
 * Every cable is a power chord: root, fifth and octave. Distortion is why rock
 * plays these rather than full chords — a fifth's partials sit on one harmonic
 * series, so clipping them together thickens into one heavy note instead of
 * breaking up into noise. A single note through the same amp is lead guitar,
 * which is what this voice used to be, and it sounded like a buzzy synth.
 */
const POWER_CHORD = [0, 7, 12];
/** A downstroke: low string first, this long between strings. */
const STRUM_S = 0.007;

/**
 * How far above the pedal's clipping point the strum lands, in dB. This is the
 * knob, and it reads as sustain: a chord stays dirty until its strings have
 * decayed this far. About 12 is crunch, 24 a pedal turned most of the way up,
 * 36 fuzz. The chord is loudness-normalised afterwards, so turning it changes
 * the dirt, not the volume.
 */
const DRIVE_DB = 3;
/** The amp after the pedal: a little more push into a softer curve. */
const AMP_DB = 6;
/** Offsets the amp's curve so it clips harder on one side, which adds the even harmonics a valve does. */
const BIAS = 0.3;

/**
 * One picked steel string, added into `out` from sample `at`.
 *
 * A chord's strings share one buffer, so the harp's trick of giving each note a
 * sample rate of its own is not available: tuning comes from a first-order
 * allpass in the loop instead, which adds any fraction of a sample of delay
 * without the damping a linear interpolator would. The loop's lowpass weights
 * its taps 3:1 rather than the harp's 1:1, so the top rings on the way steel's
 * does.
 */
function steelString(
  out: Float32Array,
  sr: number,
  freq: number,
  tail: number,
  at: number,
  level: number,
): void {
  const S = 0.25;
  // Loop delay = N whole samples + S from the lowpass + the allpass's fraction,
  // which is kept in [0.1, 1.1) where a first-order allpass behaves.
  const N = Math.max(2, Math.floor(sr / freq - S - 0.1));
  const frac = sr / freq - S - N;
  const C = (1 - frac) / (1 + frac);

  const ring = new Float32Array(N);
  pluckNoise(ring, 0.9);
  // A pick strikes about an eighth of the way along from the bridge, and a
  // string cannot move at the point it was struck from: every 8th harmonic is
  // notched out. The ring is one period, so the comb wraps around it.
  const d = Math.max(1, Math.round(N / 8));
  const copy = ring.slice();
  let mean = 0;
  for (let k = 0; k < N; k++) {
    ring[k] = (copy[k] ?? 0) - (copy[(k + d) % N] ?? 0);
    mean += ring[k] ?? 0;
  }
  mean /= N;
  let peak = 1e-6;
  for (let k = 0; k < N; k++) {
    ring[k] = (ring[k] ?? 0) - mean;
    peak = Math.max(peak, Math.abs(ring[k] ?? 0));
  }

  const R = Math.exp(Math.log(0.001) / (tail * freq));
  const g = level / peak;
  const end = Math.min(out.length, at + Math.ceil(tail * sr));
  let idx = 0;
  let prev = 0;
  let apIn = 0;
  let apOut = 0;
  for (let i = at; i < end; i++) {
    const cur = ring[idx] ?? 0;
    out[i] = (out[i] ?? 0) + g * cur;
    const lp = ((1 - S) * cur + S * prev) * R;
    prev = cur;
    apOut = C * lp + apIn - C * apOut;
    apIn = lp;
    ring[idx] = apOut;
    idx = idx + 1 === N ? 0 : idx + 1;
  }
}

/** ln(cosh x), tanh's antiderivative, without overflowing for large x. */
function logcosh(x: number): number {
  const a = Math.abs(x);
  return a > 18 ? a - Math.LN2 : a + Math.log1p(Math.exp(-2 * a)) - Math.LN2;
}

/**
 * ln cosh tabulated over ±18 with its exact slope (tanh), for cubic Hermite
 * interpolation: several times cheaper per sample than computing it, and
 * within ~1e-8. Past 18 it is exactly |x| - ln 2.
 */
const LC_SPAN = 18;
const LC_PER_UNIT = 32;
const LC_F = new Float64Array(2 * LC_SPAN * LC_PER_UNIT + 1);
const LC_D = new Float64Array(LC_F.length);
for (let k = 0; k < LC_F.length; k++) {
  const x = k / LC_PER_UNIT - LC_SPAN;
  LC_F[k] = logcosh(x);
  LC_D[k] = Math.tanh(x);
}

/** ln cosh x from the table. */
function lnCosh(x: number): number {
  if (x >= LC_SPAN || x <= -LC_SPAN) return Math.abs(x) - Math.LN2;
  const u = (x + LC_SPAN) * LC_PER_UNIT;
  const k = Math.floor(u);
  const t = u - k;
  const h = 1 / LC_PER_UNIT;
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    (2 * t3 - 3 * t2 + 1) * (LC_F[k] ?? 0) +
    (t3 - 2 * t2 + t) * h * (LC_D[k] ?? 0) +
    (3 * t2 - 2 * t3) * (LC_F[k + 1] ?? 0) +
    (t3 - t2) * h * (LC_D[k + 1] ?? 0)
  );
}

/** tanh x from the same table: the Hermite curve's own slope. */
function slope(x: number): number {
  if (x >= LC_SPAN) return 1;
  if (x <= -LC_SPAN) return -1;
  const u = (x + LC_SPAN) * LC_PER_UNIT;
  const k = Math.floor(u);
  const t = u - k;
  const t2 = t * t;
  const df = ((LC_F[k + 1] ?? 0) - (LC_F[k] ?? 0)) * LC_PER_UNIT;
  return (
    6 * (t - t2) * df +
    (3 * t2 - 4 * t + 1) * (LC_D[k] ?? 0) +
    (3 * t2 - 2 * t) * (LC_D[k + 1] ?? 0)
  );
}

/*
 * Both clippers below are antialiased the same way. Driven this hard a clipper's
 * harmonics run far past Nyquist and fold back as off-key grit, so each output
 * is the AVERAGE of the curve over the step from the previous input to this one,
 * (F(x) - F(x')) / (x - x') with F the curve's antiderivative — first-order
 * antiderivative antialiasing. When the step is too small for that division to
 * be accurate, the curve at its midpoint is the same number to within dx².
 */

/** The pedal: two diodes to ground, which is a hard clip at ±1, times `gain`. */
function pedal(out: Float32Array, gain: number): void {
  const F = (x: number) => (x > 1 ? x - 0.5 : x < -1 ? -x - 0.5 : 0.5 * x * x);
  let x1 = 0;
  let f1 = 0;
  for (let i = 0; i < out.length; i++) {
    const x = gain * (out[i] ?? 0);
    const f = F(x);
    const dx = x - x1;
    out[i] = Math.abs(dx) > 1e-3 ? (f - f1) / dx : Math.max(-1, Math.min(1, 0.5 * (x + x1)));
    x1 = x;
    f1 = f;
  }
}

/** The amp: tanh with a bias, times `gain`. */
function amp(out: Float32Array, gain: number): void {
  const offset = Math.tanh(BIAS);
  let x1 = BIAS;
  let f1 = lnCosh(x1);
  for (let i = 0; i < out.length; i++) {
    const x = gain * (out[i] ?? 0) + BIAS;
    const f = lnCosh(x);
    const dx = x - x1;
    out[i] = (Math.abs(dx) > 1e-3 ? (f - f1) / dx : slope(0.5 * (x + x1))) - offset;
    x1 = x;
    f1 = f;
  }
}

/**
 * The pedal's tone control at noon: a 400Hz lowpass and a 1.3kHz highpass
 * summed, which scoops about 6.5dB out of the mids around 720Hz and leaves the
 * lows thick and the top raw. Their phases cancel in the middle; that is the
 * whole trick.
 */
function scoop(out: Float32Array, sr: number): void {
  const aL = 1 - Math.exp((-2 * Math.PI * 400) / sr);
  const aH = 1 / (1 + (2 * Math.PI * 1300) / sr);
  let lp = 0;
  let hp = 0;
  let x1 = 0;
  for (let i = 0; i < out.length; i++) {
    const x = out[i] ?? 0;
    lp += aL * (x - lp);
    hp = aH * (hp + x - x1);
    x1 = x;
    out[i] = lp + hp;
  }
}

function rms(out: Float32Array, from: number, to: number): number {
  const end = Math.min(to, out.length);
  let e = 0;
  for (let i = from; i < end; i++) e += (out[i] ?? 0) ** 2;
  return Math.sqrt(e / Math.max(1, end - from)) + 1e-9;
}

/**
 * A strummed power chord through a distortion pedal and an amp, baked into one
 * buffer per cable. The speaker cabinet and the double-tracking are NOT in here:
 * both happen live on the way out (see `Harp.wake` and `pluck`).
 */
function renderGuitar(ctx: BaseAudioContext, freq: number): AudioBuffer {
  const sr = ctx.sampleRate;
  // Long, even at the top: the amp only holds a chord up for as long as its
  // strings stay within DRIVE_DB of the strum.
  const tail = Math.min(4.5, Math.max(2.8, 4.5 * (65 / freq) ** 0.25));
  const n = Math.ceil(sr * (tail + STRUM_S * POWER_CHORD.length));
  const buf = ctx.createBuffer(1, n, sr);
  const out = buf.getChannelData(0);

  POWER_CHORD.forEach((interval, k) => {
    // Nobody's guitar is in tune: each string lands within ±3 cents, which is
    // what makes a power chord beat and growl rather than sit there.
    const semitones = interval + (Math.random() * 2 - 1) * 0.03;
    const at = Math.round(k * STRUM_S * sr);
    steelString(out, sr, freq * 2 ** (semitones / 12), tail * (1 - 0.08 * k), at, 1 - 0.1 * k);
  });

  // A hot humbucker's resonant rolloff, and the bottom trimmed a little so the
  // lowest chords stay tight when clipped.
  lowpass(out, sr, 3500, 1.2);
  highpass(out, sr, 100);

  // Drive against the strum's own level just after the pick, not its peak: a
  // noise pluck's peak is one random spike, and the burst itself dies within
  // ~30ms. The clip point is 1, so an RMS of 1 is "at the knee".
  const level = rms(out, Math.round(sr * 0.03), Math.round(sr * 0.2));
  pedal(out, 10 ** (DRIVE_DB / 20) / level);
  scoop(out, sr);
  amp(out, 10 ** (AMP_DB / 20));
  // The bias leaves a DC shift that follows the envelope: take it back out.
  highpass(out, sr, 20);

  // Normalise loudness rather than peak. More drive means a lower crest factor,
  // so peak normalising would turn DRIVE_DB into a volume knob as well.
  const g = 0.25 / rms(out, 0, Math.round(sr * 0.4));
  for (let i = 0; i < out.length; i++) out[i] = (out[i] ?? 0) * g;
  // A driven chord is still audible when its tail runs out: a longer release.
  fadeEnds(out, sr, 48, 0.15);
  return buf;
}

// --- green: chiptune ---------------------------------------------------------------

/** How much of each cycle the pulse is high. 25% is the NES lead. */
const DUTY = 0.25;

/**
 * PolyBLEP: the two-sample polynomial that turns a pulse's instant step into a
 * band-limited one. `t` is the phase (0..1) and `dt` the phase step per sample.
 */
function blep(t: number, dt: number): number {
  if (t < dt) {
    const x = t / dt;
    return x + x - x * x - 1;
  }
  if (t > 1 - dt) {
    const x = (t - 1) / dt;
    return x * x + x + x + 1;
  }
  return 0;
}

/**
 * A 25% pulse wave with a 4-bit volume envelope and an octave-up first frame:
 * the NES lead, plucked.
 *
 * A pulse drawn sample by sample aliases: its harmonics go on forever, and each
 * one past Nyquist folds back as an off-key whine. Each edge gets a PolyBLEP
 * correction instead, and the note is drawn at the context's own rate so the
 * source node has nothing to resample. Measured in Chromium at constant level,
 * that puts the aliasing 37-46dB under the harmonics from C3 to C6, against
 * 17-27dB for the naive pulse. (Giving the period a whole number of samples,
 * the harp's trick, lands every alias on a harmonic — but only inside the
 * buffer; resampling on playback put it back to 29-39dB.)
 */
function renderChip(ctx: BaseAudioContext, freq: number): AudioBuffer {
  const sr = ctx.sampleRate;
  // Volume falls 15 -> 0 in equal steps, each a whole number of periods, and
  // only ever changes on a rising edge.
  const decay = Math.min(0.9, Math.max(0.35, 0.9 * (130 / freq) ** 0.4));
  const stepPeriods = Math.max(1, Math.round((decay / 15) * freq));
  // The first frame (1/60s) plays an octave up: the pluck of a chip lead.
  const blip = Math.max(1, Math.round(freq / 60));
  const n = Math.ceil(((15 * stepPeriods) / freq) * sr) + 1;

  const buf = ctx.createBuffer(1, n, sr);
  const out = buf.getChannelData(0);
  let phase = 0;
  let cycles = 0;
  let octave = 2;
  let vol = 1;
  // Zero-mean: high for a quarter of the cycle at +0.75, low at -0.25. Both
  // edges are steps of 1, so each gets half of blep's two-unit correction.
  for (let i = 0; i < n && vol > 0; i++) {
    const dt = (octave * freq) / sr;
    const naive = phase < DUTY ? 1 - DUTY : -DUTY;
    out[i] = vol * (naive + 0.5 * blep(phase, dt) - 0.5 * blep((phase - DUTY + 1) % 1, dt));
    phase += dt;
    if (phase >= 1) {
      phase -= 1;
      cycles++;
      if (octave === 2 && cycles === 2 * blip) {
        octave = 1;
        cycles = 0;
      }
      const periods = octave === 2 ? cycles / 2 : blip + cycles;
      vol = (15 - Math.floor(periods / stepPeriods)) / 15;
    }
  }
  fadeEnds(out, sr, 48, 0.004);
  return buf;
}

// --- blue: music box -----------------------------------------------------------------

/**
 * A steel tine is a cantilever — clamped at one end, free at the other — and
 * its overtones are not harmonics but 1 : 6.27 : 17.55 : 34.39, the squares of
 * that beam's mode numbers. That inharmonic series is the whole of the "bell".
 * [ratio, level, how long it rings as a fraction of the fundamental].
 */
const TINE: readonly (readonly [number, number, number])[] = [
  [1, 1, 1],
  [6.267, 0.45, 0.2],
  [17.55, 0.18, 0.07],
  [34.39, 0.06, 0.03],
];

function renderMusicBox(ctx: BaseAudioContext, freq: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const ring = Math.min(3.4, Math.max(0.7, 3.4 * (262 / freq) ** 0.7));
  const n = Math.ceil(sr * ring);
  const buf = ctx.createBuffer(1, n, sr);
  const out = buf.getChannelData(0);

  for (const [ratio, level, life] of TINE) {
    const f = freq * ratio;
    if (f > sr * 0.45) continue;
    // A damped resonator, y[n] = 2r·cos(w)·y[n-1] - r²·y[n-2], rings out as
    // level·rⁿ·sin(wn) with no sin() call per sample.
    const w = (2 * Math.PI * f) / sr;
    const r = Math.exp(Math.log(0.001) / (ring * life * sr));
    const c1 = 2 * r * Math.cos(w);
    const c2 = r * r;
    const len = Math.min(n, Math.ceil(ring * life * sr * 1.2));
    let y2 = 0;
    let y1 = level * r * Math.sin(w);
    for (let i = 1; i < len; i++) {
      out[i] = (out[i] ?? 0) + y1;
      const y = c1 * y1 - c2 * y2;
      y2 = y1;
      y1 = y;
    }
  }

  // The pin letting go of the tine: a few milliseconds of bright click.
  const click = Math.round(sr * 0.004);
  let prev = 0;
  for (let i = 0; i < click; i++) {
    const x = noise();
    out[i] = (out[i] ?? 0) + 0.25 * (x - prev) * Math.exp(-i / (sr * 0.0008));
    prev = x;
  }
  normalise(out);
  fadeEnds(out, sr, 24);
  return buf;
}

// --- the instrument ------------------------------------------------------------------

interface VoiceDef {
  render: (ctx: BaseAudioContext, freq: number) => AudioBuffer;
  /** Semitones added to every string. */
  transpose: number;
  /** Output trim, so switching voice does not jump in loudness. */
  level: number;
  /** Played through the shared speaker cabinet. */
  cabinet: boolean;
  /**
   * Played as two takes, one per side, the second a few cents sharp and a few
   * milliseconds late: the double-tracked wall of a grunge record, and the
   * chorus shimmer of the two copies drifting against each other.
   */
  doubled: boolean;
}

/**
 * Levels are measured, not guessed: each voice rendered through this graph in
 * Chromium, K-weighted (BS.1770), and trimmed until its loudest 400ms per pluck
 * averages the harp's. Untrimmed, the chip came out 5.4dB louder and the music
 * box 4.1dB — a beacon click would have been a jump scare. The guitar's trim
 * holds for any DRIVE_DB, because each chord is loudness-normalised after the
 * amp: at 24 and 34 it measured within 0.05dB of each other.
 */
const VOICES: Record<Voice, VoiceDef> = {
  harp: { render: renderHarp, transpose: 0, level: 1, cabinet: false, doubled: false },
  // Grunge lives low: roots C2 to C5, an octave under the harp.
  guitar: { render: renderGuitar, transpose: -12, level: 0.54, cabinet: true, doubled: true },
  chip: { render: renderChip, transpose: 0, level: 0.54, cabinet: false, doubled: false },
  // A music box lives an octave above the harp: C4 to C7.
  musicbox: { render: renderMusicBox, transpose: 12, level: 0.62, cabinet: false, doubled: false },
};

/** The buffer string `i` plays in `voice`. Exported for the synth checks. */
export function renderNote(ctx: BaseAudioContext, voice: Voice, i: number): AudioBuffer {
  const def = VOICES[voice];
  return def.render(ctx, hz(noteOf(i) + def.transpose));
}

export class Harp {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** The guitar's speaker cabinet; its output is `master`. */
  private cabinet: AudioNode | null = null;
  /** Which voice `buffers` holds. Only one is kept; the others re-render. */
  private voice: Voice = 'harp';
  private readonly buffers: (AudioBuffer | undefined)[] = [];
  /** The background render's pending timer, or 0. */
  private preparing = 0;
  /** The note still ringing on each string, so a re-pluck can damp it. */
  private readonly ringing: (GainNode | undefined)[] = [];

  /**
   * Open the audio device. Must be called from inside a user gesture, and is
   * cheap to call again — browsers suspend the context when the tab hides.
   * Returns the context clock, which is what `pluck` schedules against.
   */
  wake(): number {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return 0;
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.22;
      master.connect(ctx.destination);

      // A closed-back 4x12 is a band-pass with two bumps: the box's thump
      // around 120Hz, a presence lift around 2.5k, nothing much under 80Hz, and
      // a steep wall above 5k that is what turns the clipper's fizz into tone. Shared by every string, the
      // way one amp is — the distortion is per string, the box is not.
      const stages: [BiquadFilterType, number, number, number][] = [
        ['highpass', 80, 0.7, 0],
        ['peaking', 120, 1.2, 3],
        ['peaking', 2500, 1, 4],
        ['lowpass', 5000, 0.7, 0],
        ['lowpass', 5000, 0.7, 0],
      ];
      let into: AudioNode = master;
      for (const [type, frequency, Q, gain] of stages.reverse()) {
        const f = ctx.createBiquadFilter();
        f.type = type;
        f.frequency.value = frequency;
        f.Q.value = Q;
        f.gain.value = gain;
        f.connect(into);
        into = f;
      }

      this.ctx = ctx;
      this.master = master;
      this.cabinet = into;
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx.currentTime;
  }

  /**
   * Render every string of `voice` ahead of time, one per task, so the first
   * sweep after a switch finds them ready instead of rendering them inside its
   * own pointer events — where the guitar's ~4ms a string added up to a 100ms
   * stall on a desktop and 400ms at phone speed. Waits for the context, which
   * is what knows the device's sample rate.
   */
  prepare(voice: Voice): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.use(voice);
    window.clearTimeout(this.preparing);
    const next = () => {
      this.preparing = 0;
      if (this.ctx !== ctx || this.voice !== voice) return;
      let i = 0;
      while (i < STRING_COUNT && this.buffers[i]) i++;
      if (i === STRING_COUNT) return;
      this.buffers[i] = renderNote(ctx, voice, i);
      this.preparing = window.setTimeout(next, 0);
    };
    this.preparing = window.setTimeout(next, 0);
  }

  /** Point the cache at `voice`. Ringing notes hold their own buffers, so this cuts nothing off. */
  private use(voice: Voice): void {
    if (voice === this.voice) return;
    this.voice = voice;
    this.buffers.length = 0;
  }

  /** `at` is an absolute time on the context clock; 0 means as soon as possible. */
  pluck(i: number, at = 0, gain = 1, voice: Voice = 'harp'): void {
    const ctx = this.ctx;
    const master = this.master;
    const cabinet = this.cabinet;
    if (!ctx || !master || !cabinet || i < 0 || i >= STRING_COUNT) return;
    const when = Math.max(at, ctx.currentTime);
    const def = VOICES[voice];

    this.use(voice);
    let buf = this.buffers[i];
    if (!buf) {
      buf = renderNote(ctx, voice, i);
      this.buffers[i] = buf;
    }

    // Touching a ringing string damps it, the way a finger would.
    const ringing = this.ringing[i];
    if (ringing) {
      ringing.gain.cancelScheduledValues(when);
      ringing.gain.setTargetAtTime(0, when, 0.014);
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;
    // No two plucks of one string are identical, and an exact repeat reads as a
    // sample being retriggered rather than as a string being played. Kept under
    // three cents, which is variation rather than mistuning.
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * 0.0015;

    const vca = ctx.createGain();
    vca.gain.value = gain * def.level * (0.72 + Math.random() * 0.28);
    vca.connect(def.cabinet ? cabinet : master);

    // The fan is nearly a screen wide, so where a cable is heard follows where
    // it is seen.
    const where = (i / (STRING_COUNT - 1)) * 1.1 - 0.55;
    const pan = ctx.createStereoPanner();
    src.connect(pan).connect(vca);
    const nodes: AudioNode[] = [src, pan, vca];
    const sources = [src];

    if (def.doubled) {
      // Two takes, one each side. Both still lean toward the cable, and the
      // second is 6 cents sharp and 14ms late, so the copies drift in and out
      // of phase instead of combing. Halved, so a double is not twice as loud.
      pan.pan.value = -0.7 + where * 0.4;
      const take = ctx.createBufferSource();
      take.buffer = buf;
      take.playbackRate.value = src.playbackRate.value * 2 ** (6 / 1200);
      const side = ctx.createStereoPanner();
      side.pan.value = 0.7 + where * 0.4;
      take.connect(side).connect(vca);
      take.start(when + 0.014);
      vca.gain.value *= 0.5;
      nodes.push(take, side);
      sources.push(take);
    } else {
      pan.pan.value = where;
    }

    src.start(when);
    this.ringing[i] = vca;
    // Which take ends first depends on the note's length (the second starts
    // later but plays faster), so tear down when both have.
    let playing = sources.length;
    for (const source of sources) {
      source.onended = () => {
        if (--playing > 0) return;
        if (this.ringing[i] === vca) this.ringing[i] = undefined;
        for (const node of nodes) node.disconnect();
      };
    }
  }

  dispose(): void {
    window.clearTimeout(this.preparing);
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.cabinet = null;
  }
}
